import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Live "verify a real credential" widget for the landing page. Fetches a genuine
 * (and a revoked) demo credential from the SAME public endpoint the real
 * /verify/:guid page uses — so it's a real verification, not a mock. The demo
 * credentials are self-issued by badges.ninja (issuer 1f87a155).
 */
const API_URL = (import.meta.env.VITE_CERTIFY_BASE_URL || import.meta.env.VITE_API_URL || 'https://api.badges.ninja') as string;

const GENUINE_GUID = '9831f64e-5fc7-4742-8d3d-0a0892d9ec35';
const REVOKED_GUID = 'ac091734-9f8b-4df6-839d-a673bbdda969';

interface Assertion {
  image?: string;
  issuedOn?: string;
  recipient?: { name?: string };
  badge?: { name?: string; issuer?: { name?: string } };
  revoked?: boolean;
  revocationReason?: string;
}

type Status = 'idle' | 'loading' | 'done' | 'error';

export function VerifyDemo() {
  const { t, i18n } = useTranslation('landing');
  const [status, setStatus] = useState<Status>('idle');
  const [which, setWhich] = useState<'genuine' | 'revoked'>('genuine');
  const [award, setAward] = useState<Assertion | null>(null);

  async function verify(target: 'genuine' | 'revoked') {
    const guid = target === 'genuine' ? GENUINE_GUID : REVOKED_GUID;
    setWhich(target);
    setStatus('loading');
    setAward(null);
    try {
      const res = await fetch(`${API_URL}/certify-badge/award/${guid}`);
      if (!res.ok) throw new Error(String(res.status));
      const data: Assertion = await res.json();
      setAward(data);
      setStatus('done');
    } catch {
      setStatus('error');
    }
  }

  const guid = which === 'genuine' ? GENUINE_GUID : REVOKED_GUID;
  const revoked = !!award?.revoked;
  const fmtDate = (iso?: string) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return iso.slice(0, 10); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8">
      <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:gap-8">
        {/* Credential preview */}
        <div className="flex shrink-0 flex-col items-center gap-3">
          <div className="flex h-28 w-28 items-center justify-center rounded-xl border border-white/10 bg-white/5 p-2">
            {award?.image
              ? <img src={award.image} alt="" className={`h-full w-full object-contain ${revoked ? 'opacity-40 grayscale' : ''}`} />
              : <img src="/assets/badge-templates/generated/gen-recognition-blue-preview.png" alt="" className="h-full w-full object-contain opacity-70" />}
          </div>
          <span className="text-xs text-gray-500">{t('verifyDemo.sampleLabel', 'Sample credential')}</span>
        </div>

        {/* Live result */}
        <div className="flex-1 text-center sm:text-left" aria-live="polite">
          {status === 'idle' && (
            <>
              <h3 className="text-lg font-semibold text-white">{t('verifyDemo.heading', "Don't take our word for it")}</h3>
              <p className="mt-1 text-sm text-gray-400">{t('verifyDemo.sub', 'Verify a real credential live, straight from this page — the same check anyone can run.')}</p>
            </>
          )}
          {status === 'loading' && (
            <p className="text-sm text-gray-300">{t('verifyDemo.checking', 'Checking the issuer’s record…')}</p>
          )}
          {status === 'error' && (
            <p className="text-sm text-amber-300">{t('verifyDemo.error', 'Verification is temporarily unavailable. Please try again in a moment.')}</p>
          )}
          {status === 'done' && award && (
            <>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${revoked ? 'bg-red-500/10 text-red-300' : 'bg-green-500/10 text-green-300'}`}>
                {revoked
                  ? <>✕ {t('verifyDemo.revoked', 'Revoked credential')}</>
                  : <>✓ {t('verifyDemo.genuine', 'Genuine credential')}</>}
              </span>
              <p className="mt-3 text-base font-semibold text-white">{award.badge?.name}</p>
              <p className="mt-0.5 text-sm text-gray-400">
                {t('verifyDemo.issuedBy', 'Issued by')} <span className="text-gray-200">{award.badge?.issuer?.name}</span>{' '}
                {t('verifyDemo.to', 'to')} <span className="text-gray-200">{award.recipient?.name}</span> · {fmtDate(award.issuedOn)}
              </p>
              {revoked && award.revocationReason && (
                <p className="mt-1 text-xs text-red-300/80">{award.revocationReason}</p>
              )}
            </>
          )}

          {/* Actions */}
          <div className="mt-5 flex flex-col items-center gap-3 sm:flex-row sm:items-center">
            {status !== 'done' ? (
              <button
                onClick={() => verify('genuine')}
                disabled={status === 'loading'}
                className="rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-purple-500 disabled:opacity-60"
              >
                {status === 'loading' ? t('verifyDemo.verifying', 'Verifying…') : t('verifyDemo.cta', 'Verify this credential')}
              </button>
            ) : (
              <a
                href={`/verify/${guid}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-white/10 px-5 py-2.5 text-sm font-medium text-gray-200 transition hover:bg-white/5"
              >
                {t('verifyDemo.openFull', 'Open the full verification page →')}
              </a>
            )}
            <button
              onClick={() => verify(which === 'genuine' && status === 'done' ? 'revoked' : 'genuine')}
              className="text-sm font-medium text-purple-400 transition hover:text-purple-300"
            >
              {which === 'genuine' && status === 'done'
                ? t('verifyDemo.tryRevoked', 'Try a revoked one →')
                : t('verifyDemo.tryGenuine', 'Verify a genuine one →')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
